import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../Button/Button';
import { Heading } from '../Heading';
import { Image } from 'shared/types';
import { getEpisodeLabel } from 'shared/utils/getEpisodeLabel';
import { CategoryTag } from 'shared/data/episodes';

export interface EpisodeCardProps {
  image: Image;
  category: CategoryTag;
  number: string;
  title: string;
  description: string;
  onPlay?: () => void;
}

export function EpisodeCard({
  image,
  category,
  number,
  title,
  description,
  onPlay,
}: EpisodeCardProps) {
  const { t } = useTranslation();

  const categoryTags = Array.isArray(category) ? category : [category];

  return (
    <article
      className="group relative flex flex-col bg-surface-container-low rounded-xl overflow-hidden transform-gpu transition-transform duration-300 hover:-translate-y-1"
    >
      {/* Image */}
      <div className="relative h-64 overflow-hidden">
        <img
          src={image.src}
          alt={image.alt}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
        />
        {category && (
          <div className="absolute top-4 left-4 flex flex-row gap-2">
            {categoryTags.map((tag, index) => (
              <div
                key={`${tag}-${index}`}
                className="rounded-full bg-tertiary px-3 py-1 shadow-md dark:border-outline-variant/50 dark:bg-surface-container-lowest"
              >
                <span className="font-sans text-xs font-bold uppercase tracking-widest text-on-tertiary dark:text-tertiary">
                  {tag}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-8 flex flex-col flex-grow">
        <div className="flex items-center justify-between mb-4">
          <span className="text-on-surface-variant font-sans text-xs uppercase tracking-tighter">
            {getEpisodeLabel(number)}
          </span>
        </div>

        <Heading variant="card" className="font-bold text-on-surface mb-3 group-hover:text-tertiary transition-colors">
          <Link to={`/episodes/${number}`} className="after:absolute after:inset-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tertiary focus-visible:ring-offset-2 focus-visible:rounded-xl">
            {title}
          </Link>
        </Heading>

        <p className="text-on-surface-variant text-sm font-light leading-relaxed mb-8">
          {description}
        </p>

        {/* Actions */}
        <div className="mt-auto pt-6 flex items-center justify-between border-t border-outline-variant/10">
          {onPlay ? (
            <Button
              variant="link"
              size="sm"
              icon="play-circle"
              iconWeight="fill"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onPlay();
              }}
              className="relative z-10"
            >
              {t('listenNow')}
            </Button>
          ) : (
            <Button
              variant="link"
              size="sm"
              icon="play-circle"
              iconWeight="fill"
              to={`/episodes/${number}`}
              className="relative z-10"
            >
              {t('listenNow')}
            </Button>
          )}
        </div>
      </div>
    </article>
  );
}
